import { CDP, } from '@squareup/cdp';
import { initializeEventCapturing } from '@squareup/service-debug-event-capture';
import { OneTrustConsent, hasConsent, onFirstConsent } from '@squareup/service-one-trust';
import { getCookie } from '@squareup/util-cookies';
export const CDP_PRODUCTION_KEY = 'b98a8c1e-125a-4926-aad8-92cff52424d6';
export const CDP_STAGING_KEY = '7ef681e6-f122-43b0-a9f2-2babf9475bc8';
export default class TrackingClient {
    cdp = null;
    userInfo = null;
    shell = undefined;
    constructor({ userInfo, shell, environment = 'staging', apiKey, useSameOrigin, ...cdpOptions }) {
        this.userInfo = userInfo;
        this.shell = shell;
        const resolvedApiKey = apiKey ?? (environment === 'production' ? CDP_PRODUCTION_KEY : CDP_STAGING_KEY);
        const resolvedSameOrigin = useSameOrigin ?? (environment === 'production' || environment === 'staging');
        this.cdp = new CDP({
            ...cdpOptions,
            environment,
            apiKey: resolvedApiKey,
            useSameOrigin: resolvedSameOrigin,
        });
        initializeEventCapturing({
            environment,
            initializingFramework: 'react',
            shell,
            userContext: {
                merchantToken: userInfo.merchantToken,
                employmentToken: userInfo.employmentToken,
                personToken: userInfo.personToken,
                loggableSessionToken: userInfo.loggableSessionToken,
            },
        });
    }
    /**
     * identify is called on application load, so you probably don't have to call it yourself.
     * but you can if you want to add additional traits to identify the merchant.
     *
     * The call is deferred until OneTrust TARGETING consent is granted. If consent
     * is already present the identify fires synchronously; otherwise it will fire
     * as soon as the user grants consent.
     *
     * @param customTraits
     * @param options
     * @returns A cleanup function that removes the consent listener (no-op if consent was already granted).
     */
    identify = (customTraits = {}, options = {}) => {
        const cleanup = onFirstConsent(OneTrustConsent.TARGETING, () => {
            const { merchantToken, employmentToken, personToken } = this.userInfo;
            if (!merchantToken) {
                console.error(new Error('Tried to call cdp identify without a merchant token'));
                return;
            }
            const traits = {
                ...customTraits,
                merchantToken,
                employmentToken,
                personToken,
            };
            const optimizelyIdCookie = getCookie('optimizelyEndUserId');
            if (optimizelyIdCookie && !traits.optimizely_visitor_id) {
                traits.optimizely_visitor_id = JSON.stringify([optimizelyIdCookie, navigator.userAgent]);
            }
            this.cdp?.identifyStrict({
                entityId: merchantToken,
                entityType: 'merchant',
                traits,
                options,
            });
        });
        return cleanup;
    };
    /**
     *
     * @param eventName
     * @param properties
     * @param context
     * @returns
     */
    track = (eventName, properties = {}, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    context = {}) => {
        if (!eventName) {
            console.error(new Error('Tried to call cdp track without an event name'));
            return;
        }
        if (!hasConsent(OneTrustConsent.TARGETING)) {
            return;
        }
        const updatedContext = {
            ...context,
            sessionId: this.userInfo?.loggableSessionToken,
            ...(this.shell && { shell: this.shell, codebase: 'dashboard web' }),
        };
        // TODO: This should use trackWithSchema
        this.cdp?.trackStrict({
            eventName,
            eventProps: properties,
            options: { overrides: { context: updatedContext } },
        });
    };
    /**
     *
     * @param name
     * @param properties
     * @param context
     * @returns
     */
    page = (name, properties = {}, 
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    context = {}) => {
        if (!hasConsent(OneTrustConsent.TARGETING)) {
            return;
        }
        const updatedContext = {
            ...context,
            sessionId: this.userInfo?.loggableSessionToken,
            ...(this.shell && { shell: this.shell, codebase: 'dashboard web' }),
        };
        this.cdp?.pageStrict({
            pageName: name,
            eventProps: properties,
            options: {
                overrides: { context: updatedContext },
            },
        });
    };
    /**
     *
     * @param event
     * @param context
     * @returns
     */
    trackWithSchema = (event, context = {}) => {
        if (!event) {
            console.error(new Error('Tried to call cdp trackWithSchema without an event'));
            return;
        }
        if (!hasConsent(OneTrustConsent.TARGETING)) {
            return;
        }
        const overrides = {
            ...context,
            sessionId: this.userInfo?.loggableSessionToken,
        };
        this.cdp?.trackWithSchema(event, {
            overrides,
        });
    };
}
